import { extractAxiosErrorDetail } from '@heidi/api/errors';
import {
  FILE_UPLOAD_ERROR_CODES,
  type FileUploadRejection,
} from '@heidi/design-system/file-upload';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { isAxiosError } from 'axios';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { CollectionKind } from '../../../../contracts';
import { getMaxDomainsPerCollection } from '../../../../contracts';
import {
  useAddDocumentsToCollection,
  useCollections,
  useCreateCollection,
  useCreateSourceList,
  useSourceLists,
} from '../../../../data';
import { isUnifiedCollectionsEnabled } from '../../../../data/injected';
import { createLibraryUploadFile, type LibraryUploadFile } from '../../upload.types';
import { useFileValidation } from '../../UploadDocumentModalComponents.helpers';
import { useUploadUserDocument } from '../../user-documents/hooks/useUploadUserDocument';
import type { SelectedExistingDoc } from '../DocumentSearchInput';
import { generateDefaultCollectionName } from '../utils/generateDefaultCollectionName';

/** Pick the tone for the save-success toast.
 *
 * `totalItems` is *anything* persisted on the collection — successful
 * document uploads PLUS attached existing docs PLUS staged web-source
 * domains. The previous version only counted documents, so a web-only
 * unified collection (293 domains, 0 docs) with any doc-upload failure
 * was reported as `destructive` even though nothing actually failed for
 * the user's actual payload. Counting domains lets us distinguish
 * "nothing got saved" (destructive) from "everything got saved" (success)
 * regardless of which kind dominates. */
function uploadResultTone(totalItems: number, failedUploads: number) {
  if (totalItems === 0 && failedUploads > 0) return 'destructive' as const;
  if (failedUploads > 0) return 'default' as const;
  return 'success' as const;
}

export type WizardStep = 'entry' | 'web-sources' | 'documents';

export interface WizardState {
  step: WizardStep;
  name: string;
  isTeamCollection: boolean;
  addedDomains: string[];
  stagedFiles: LibraryUploadFile[];
  selectedExistingDocs: SelectedExistingDoc[];
}

/**
 * The team-scoping facts the wizard needs, resolved at the mount.
 *
 * They come from the app's `useTeamEvidenceEnforcement()` — an app hook over
 * the org policy — so the RESOLVED values cross rather than the hook. Every
 * mount passes its own, which is what keeps the enforcement verdict a single
 * app-side resolution.
 */
export interface WizardTeamContext {
  /** Whether this clinician may create a TEAM collection at all. */
  canCreateTeamCollection: boolean;
  /** The enforcement's team label, used when the team query has none. */
  teamName?: string;
}

interface UseCreateCollectionWizardOptions {
  initialStep?: WizardStep;
  /** See {@link WizardTeamContext}. */
  teamContext: WizardTeamContext;
  /** When true, the created collection will be scoped to the user's team. */
  defaultTeam?: boolean;
  /** Pre-populate the domain list (e.g. from source recommendations). */
  initialDomains?: string[];
  /** Pre-populate the collection name. */
  initialName?: string;
  /**
   * Fired after a collection (web or documents) is successfully created.
   *
   * `domainCount` reflects what was actually persisted:
   * - `type: 'web'`  → number of domains added.
   * - `type: 'documents'` → number of documents (staged uploads +
   *   selected existing) added.
   *
   * `savedDomains` is the full domain list actually persisted. Empty
   * for `type: 'documents'` (those are docs, not web sources). Used by
   * callers like the source-recommendation banner to compute the
   * customisation diff against what they originally suggested
   * (`removed_domains` / `added_domains` on the Applied event).
   *
   * Carrying both back to the caller lets analytics events (e.g.
   * `SourceRecommendationApplied` on the customise-complete path) emit
   * the true post-save size + diff without re-querying the collection.
   */
  onCollectionCreated?: (collection: {
    id: string;
    type: 'documents' | 'web';
    domainCount: number;
    savedDomains: string[];
  }) => void;
  onClose: () => void;
  /** Work-output folder vs Evidence source collection. Default `input`. */
  kind?: CollectionKind;
}

export function useCreateCollectionWizard({
  initialStep = 'entry',
  teamContext,
  defaultTeam = false,
  initialDomains = [],
  initialName = '',
  onCollectionCreated,
  onClose,
  kind = 'input',
}: UseCreateCollectionWizardOptions) {
  const { t } = useTranslations();
  const intl = useIntl();
  const validateFile = useFileValidation();
  const { hasTeam, teamDetails, isLoadingTeamDetails } =
    useCombinedQueriesTeamGeneralInfo();
  const { canCreateTeamCollection, teamName } = teamContext;

  // Fetch existing names to compute a unique default
  const { data: collectionsData } = useCollections();
  const { data: sourceListsData } = useSourceLists();

  const defaultName = useMemo(() => {
    const collectionNames = (collectionsData?.collections ?? []).map((c) => c.name);
    const sourceListNames = (sourceListsData ?? []).map((sl) => sl.name);
    return generateDefaultCollectionName(
      [...collectionNames, ...sourceListNames],
      t('library.wizard.defaultCollectionName')
    );
  }, [collectionsData, sourceListsData, t]);

  // Per-collection domain cap. Resolved once at mount: the flag-driven
  // ceiling doesn't change inside a wizard session, and capturing it in
  // state avoids the flag being re-read on every render. Mirrors the BE
  // Pydantic validators — see
  // `packages/evidence/src/contracts/library/constants.ts` for the cap
  // rationale. The flag is read HERE and passed in: the helper is an inert
  // contract and cannot reach a flag client.
  const [maxDomains] = useState<number>(() =>
    getMaxDomainsPerCollection(isUnifiedCollectionsEnabled())
  );

  const [state, setState] = useState<WizardState>({
    step: initialStep,
    name: initialName,
    isTeamCollection: defaultTeam && canCreateTeamCollection,
    // Pre-cap the seed list so the wizard never displays sources that the
    // backend would silently drop. The recommendation banner sorts its
    // payload best-first (anchors → regional → non-regional, each ranked
    // by authoritativeness), so slicing here keeps the most useful
    // sources. Trying to add more later triggers the cap dialog.
    addedDomains: initialDomains.slice(0, maxDomains),
    stagedFiles: [],
    selectedExistingDocs: [],
  });
  const [error, setError] = useState<string | null>(null);
  // Surface a modal dialog when the user attempts to add past the cap.
  // Owned by the hook so both wizard surfaces (`CreateCollectionWizard`,
  // `UnifiedCreateCollectionModal`) can render the same dialog without
  // each duplicating state.
  const [isCapDialogOpen, setIsCapDialogOpen] = useState(false);

  // Derive the display name: user-edited name takes precedence, otherwise use computed default
  const displayName = state.name || defaultName;

  const hasEntryStep = initialStep === 'entry';

  // Mutations
  const { mutateAsync: createSourceList, isPending: isCreatingSourceList } =
    useCreateSourceList();
  const { mutateAsync: createCollection, isPending: isCreatingCollection } =
    useCreateCollection();
  const { mutateAsync: uploadDocument, isPending: isUploadingDocument } =
    useUploadUserDocument();
  const { mutateAsync: addDocsToCollection, isPending: isAddingDocs } =
    useAddDocumentsToCollection();

  const isTeamDataPending = state.isTeamCollection && isLoadingTeamDetails;
  const isSaving =
    isCreatingSourceList ||
    isCreatingCollection ||
    isUploadingDocument ||
    isAddingDocs ||
    isTeamDataPending;

  // ---------------------------------------------------------------------------
  // State updaters
  // ---------------------------------------------------------------------------

  const setStep = useCallback((step: WizardStep) => {
    setState((prev) => ({ ...prev, step }));
    setError(null);
  }, []);

  const setName = useCallback((name: string) => {
    setState((prev) => ({ ...prev, name }));
  }, []);

  const setIsTeamCollection = useCallback((isTeamCollection: boolean) => {
    setState((prev) => ({ ...prev, isTeamCollection }));
  }, []);

  const addDomain = useCallback(
    (domain: string) => {
      let shouldOpenCapDialog = false;
      setState((prev) => {
        if (prev.addedDomains.includes(domain)) return prev;
        // Cap reached — leave state untouched so the user gets an explicit
        // "remove one first" prompt instead of a silent no-op that looks
        // like a broken add button. The cap-dialog open is performed
        // outside this updater (see below) so it doesn't violate React's
        // rule against side effects in state updaters (StrictMode
        // double-invokes updaters to surface exactly this bug).
        if (prev.addedDomains.length >= maxDomains) {
          shouldOpenCapDialog = true;
          return prev;
        }
        return { ...prev, addedDomains: [...prev.addedDomains, domain] };
      });
      if (shouldOpenCapDialog) setIsCapDialogOpen(true);
    },
    [maxDomains]
  );

  /** Append multiple domains in a single state update, deduplicating against
   *  what's already in `addedDomains` and within the input list. When the
   *  batch would push past `maxDomains`, the first N net-new domains are
   *  accepted (filling remaining slots) and the dialog is raised so the
   *  user knows the rest were dropped. */
  const addDomains = useCallback(
    (domains: string[]) => {
      if (domains.length === 0) return;
      let shouldOpenCapDialog = false;
      setState((prev) => {
        const existing = new Set(prev.addedDomains);
        const netNew: string[] = [];
        for (const d of domains) {
          if (existing.has(d)) continue;
          existing.add(d);
          netNew.push(d);
        }
        if (netNew.length === 0) return prev;
        const remaining = Math.max(0, maxDomains - prev.addedDomains.length);
        const additions = netNew.slice(0, remaining);
        if (netNew.length > additions.length) {
          // Some net-new domains were dropped because of the cap — flag
          // the dialog so the user is informed instead of silently losing
          // them. Dialog open is performed outside this updater to avoid
          // side effects inside `setState` (React rules / StrictMode).
          shouldOpenCapDialog = true;
        }
        if (additions.length === 0) return prev;
        return { ...prev, addedDomains: [...prev.addedDomains, ...additions] };
      });
      if (shouldOpenCapDialog) setIsCapDialogOpen(true);
    },
    [maxDomains]
  );

  const removeDomain = useCallback((domain: string) => {
    setState((prev) => ({
      ...prev,
      addedDomains: prev.addedDomains.filter((d) => d !== domain),
    }));
  }, []);

  /** Validate a batch of files. Returns valid files, or sets an error and returns [].
   *  When `stopOnFirstError` is true the remaining files are skipped on first failure. */
  const getValidFiles = useCallback(
    (files: LibraryUploadFile[], stopOnFirstError = false): LibraryUploadFile[] => {
      const valid: LibraryUploadFile[] = [];
      for (const file of files) {
        const result = validateFile(file);
        if (result.valid) {
          valid.push(file);
        } else {
          setError(result.error);
          if (stopOnFirstError) return [];
        }
      }
      return valid;
    },
    [validateFile]
  );

  const addFiles = useCallback(
    (files: LibraryUploadFile[]) => {
      const validFiles = getValidFiles(files);
      if (validFiles.length > 0) {
        setState((prev) => ({
          ...prev,
          stagedFiles: [...prev.stagedFiles, ...validFiles],
        }));
        setError(null);
      }
    },
    [getValidFiles]
  );

  /**
   * Map FileDropzone rejection codes to wizard error messages. The DS
   * primitive filters by `accept` / `maxSize` before our `validateFile`
   * can run, so without this `.doc` (and other invalid types) would be
   * silently dropped with no user feedback.
   */
  const handleFilesRejected = useCallback(
    (rejections: FileUploadRejection[]) => {
      const errorCodes = new Set(rejections.flatMap((r) => r.errors.map((e) => e.code)));

      if (errorCodes.has(FILE_UPLOAD_ERROR_CODES.TOO_MANY_FILES)) {
        setError(
          intl.formatMessage({
            defaultMessage: 'Please upload one file at a time.',
            id: 'FZ4cafkzxE',
          })
        );
        return;
      }

      // For invalid-type / too-large rejections, defer to `validateFile`
      // so the user sees the same message the upload validator would emit
      // — including the legacy `.doc` re-save hint.
      const firstFile = rejections[0]?.file;
      if (firstFile) {
        const { error } = validateFile(createLibraryUploadFile(firstFile));
        if (error) {
          setError(error);
          return;
        }
      }
      setError(
        intl.formatMessage({
          defaultMessage: 'This file can’t be uploaded.',
          id: 'YouZ8p3+9u',
        })
      );
    },
    [intl, validateFile]
  );

  const removeFile = useCallback((uid: string) => {
    setState((prev) => ({
      ...prev,
      stagedFiles: prev.stagedFiles.filter((f) => f.uid !== uid),
    }));
  }, []);

  const addExistingDocument = useCallback((doc: SelectedExistingDoc) => {
    setState((prev) => {
      if (prev.selectedExistingDocs.some((d) => d.id === doc.id)) return prev;
      return { ...prev, selectedExistingDocs: [...prev.selectedExistingDocs, doc] };
    });
  }, []);

  const removeExistingDocument = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      selectedExistingDocs: prev.selectedExistingDocs.filter((d) => d.id !== id),
    }));
  }, []);

  // ---------------------------------------------------------------------------
  // Step transitions
  // ---------------------------------------------------------------------------

  /** From Step 1: user clicked a search result → go to Step 2A with source pre-added */
  const selectSourceAndTransition = useCallback(
    (domain: string) => {
      let shouldOpenCapDialog = false;
      setState((prev) => {
        if (prev.addedDomains.includes(domain)) {
          return { ...prev, step: 'web-sources' };
        }
        // Entry-step click that lands at the cap — transition to the
        // web-sources step (so the user can curate) but flag the dialog
        // so the silently-dropped add is acknowledged. Dialog open is
        // performed outside this updater (React rules / StrictMode).
        if (prev.addedDomains.length >= maxDomains) {
          shouldOpenCapDialog = true;
          return { ...prev, step: 'web-sources' };
        }
        return {
          ...prev,
          step: 'web-sources',
          addedDomains: [...prev.addedDomains, domain],
        };
      });
      if (shouldOpenCapDialog) setIsCapDialogOpen(true);
      setError(null);
    },
    [maxDomains]
  );

  /** From Step 1: user dropped/selected files → go to Step 2B with files staged */
  const stageFilesAndTransition = useCallback(
    (files: LibraryUploadFile[]) => {
      const validFiles = getValidFiles(files, true);
      if (validFiles.length > 0) {
        setState((prev) => ({
          ...prev,
          step: 'documents',
          stagedFiles: [...prev.stagedFiles, ...validFiles],
        }));
        setError(null);
      }
    },
    [getValidFiles]
  );

  /** Go back to entry step (Evidence flow only) */
  const goBack = useCallback(() => {
    if (!hasEntryStep) return;
    setState((prev) => ({
      ...prev,
      step: 'entry',
      // Keep addedDomains/stagedFiles so user doesn't lose progress
    }));
    setError(null);
  }, [hasEntryStep]);

  // ---------------------------------------------------------------------------
  // Save handlers
  // ---------------------------------------------------------------------------

  const teamId =
    state.isTeamCollection && hasTeam && canCreateTeamCollection
      ? teamDetails?.team.id
      : undefined;

  /**
   * Uploads all staged files into the just-created collection and returns the
   * number of failed uploads.
   *
   * Two things are critical here (DEV-11499):
   * 1. Documents must inherit the collection's scope. A team collection needs
   *    team-scoped documents or the backend can't attach them after
   *    processing. We derive the scope from the resolved `teamId` (not the raw
   *    `isTeamCollection` toggle) so the document scope can never diverge from
   *    the scope the collection was actually created with - e.g. when the
   *    team-creation gate falls the collection back to personal. `teamId`
   *    itself is forwarded only for the optimistic in-flight card; the
   *    multipart upload intentionally omits `team_id` (like
   *    `updateUserDocumentScope`) and the backend infers the team from the
   *    authenticated user + `scope`.
   * 2. The returned job IDs are recorded against the collection id so the
   *    collection detail page can show the uploads as processing cards
   *    immediately, instead of waiting for the async job + backend auto-add.
   */
  const uploadStagedFilesToCollection = useCallback(
    async (collectionId: string): Promise<number> => {
      if (state.stagedFiles.length === 0) return 0;

      const docScope = teamId ? 'team' : 'user';

      const results = await Promise.allSettled(
        state.stagedFiles.map((file) =>
          uploadDocument({
            file,
            collectionId,
            scope: docScope,
            teamId: teamId ?? null,
          })
        )
      );

      return results.filter((r) => r.status === 'rejected').length;
    },
    [state.stagedFiles, teamId, uploadDocument]
  );

  const saveWebCollection = useCallback(async () => {
    const collectionName = state.name.trim() || defaultName;
    setError(null);

    try {
      const result = await createSourceList({
        name: collectionName,
        domains: state.addedDomains,
        team_id: teamId,
      });

      if (result?.id) {
        toast({
          tone: 'success',
          title: t('webCollections.createModal.successTitle'),
          description: t(
            state.addedDomains.length === 1
              ? 'webCollections.createModal.successDescription'
              : 'webCollections.createModal.successDescriptionPlural',
            { name: collectionName, count: state.addedDomains.length }
          ),
        });
        onCollectionCreated?.({
          id: result.id,
          type: 'web',
          domainCount: state.addedDomains.length,
          savedDomains: state.addedDomains,
        });
        onClose();
      }
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        setError(t('webCollections.createModal.duplicateError'));
      } else {
        toast({
          tone: 'destructive',
          title: t('webCollections.createModal.createError'),
          description: extractAxiosErrorDetail(err),
        });
      }
    }
  }, [
    state.name,
    state.addedDomains,
    defaultName,
    createSourceList,
    teamId,
    t,
    onCollectionCreated,
    onClose,
  ]);

  const saveDocumentCollection = useCallback(async () => {
    const collectionName = state.name.trim() || defaultName;
    setError(null);

    try {
      // 1. Create the collection
      const collection = await createCollection({
        name: collectionName,
        team_id: teamId,
      });

      // 2. Upload all staged files with the collection ID
      const failedUploads = await uploadStagedFilesToCollection(collection.collection_id);

      // 3. Add existing library documents to the collection
      if (state.selectedExistingDocs.length > 0) {
        await addDocsToCollection({
          collectionId: collection.collection_id,
          documentIds: state.selectedExistingDocs.map((d) => d.id),
        });
      }

      const totalDocs =
        state.stagedFiles.length - failedUploads + state.selectedExistingDocs.length;
      toast({
        title: t('library.wizard.createSuccess.title'),
        description: t(
          `library.wizard.createSuccess.description_${totalDocs === 1 ? 'one' : 'other'}`,
          { name: collectionName, count: totalDocs }
        ),
        tone: uploadResultTone(totalDocs, failedUploads),
      });

      onCollectionCreated?.({
        id: collection.collection_id,
        type: 'documents',
        domainCount: totalDocs,
        // Document collections have no web sources to diff against —
        // callers compute customisation against the empty set.
        savedDomains: [],
      });
      onClose();
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        setError(t('library.wizard.duplicateError'));
      } else {
        toast({
          tone: 'destructive',
          title: t('library.wizard.createError'),
          description: extractAxiosErrorDetail(err),
        });
      }
    }
  }, [
    state.name,
    state.stagedFiles,
    state.selectedExistingDocs,
    defaultName,
    createCollection,
    teamId,
    uploadStagedFilesToCollection,
    addDocsToCollection,
    t,
    onCollectionCreated,
    onClose,
  ]);

  /** Unified save — creates a single collection with optional domains,
   *  then uploads staged files and adds existing documents. */
  const saveCollection = useCallback(async () => {
    const collectionName = state.name.trim() || defaultName;
    setError(null);

    try {
      const collection = await createCollection({
        name: collectionName,
        team_id: teamId,
        ...(kind === 'artifacts'
          ? { kind: 'artifacts' }
          : state.addedDomains.length > 0
            ? { domains: state.addedDomains }
            : {}),
      });

      const isArtefactFolder = kind === 'artifacts';
      const failedUploads = isArtefactFolder
        ? 0
        : await uploadStagedFilesToCollection(collection.collection_id);

      if (!isArtefactFolder && state.selectedExistingDocs.length > 0) {
        await addDocsToCollection({
          collectionId: collection.collection_id,
          documentIds: state.selectedExistingDocs.map((d) => d.id),
        });
      }

      // Three success-toast shapes, picked by what was actually persisted
      // (NOT just documents — the bug was using a docs-only count for a
      // collection that may be web-sources-only, docs-only, or mixed):
      //   - sources-only  → "...created with N source(s)." (legacy copy)
      //   - docs-only     → "...created with N document(s)." (existing copy)
      //   - mixed         → emit both toasts; trying to combine them in a
      //                     single sentence forces a pluralization matrix
      //                     ("1 documents and 5 sources") that no existing
      //                     copy handles cleanly. Two stacked toasts stay
      //                     accurate and reuse the per-kind strings.
      const totalDocs =
        state.stagedFiles.length - failedUploads + state.selectedExistingDocs.length;
      const totalSources = state.addedDomains.length;
      const totalItems = totalDocs + totalSources;
      const tone = uploadResultTone(totalItems, failedUploads);

      if (totalDocs > 0) {
        toast({
          title: t('library.wizard.createSuccess.title'),
          description: t(
            `library.wizard.createSuccess.description_${totalDocs === 1 ? 'one' : 'other'}`,
            { name: collectionName, count: totalDocs }
          ),
          tone,
        });
      }
      if (totalSources > 0) {
        toast({
          title: t('library.wizard.createSuccess.title'),
          description: t(
            `library.wizard.createSuccess.description_sources_${
              totalSources === 1 ? 'one' : 'other'
            }`,
            { name: collectionName, count: totalSources }
          ),
          // Sources never fail the way uploads can — they're saved
          // atomically as part of the POST /evidence/collections payload.
          // If we reached this branch the sources are in.
          tone: 'success',
        });
      }
      if (totalItems === 0) {
        // Empty collection (name only) — keep the original docs-flavoured
        // toast so the user still sees a confirmation.
        toast({
          title: t('library.wizard.createSuccess.title'),
          description: t('library.wizard.createSuccess.description_other', {
            name: collectionName,
            count: 0,
          }),
          tone,
        });
      }

      // Unified collections can mix domains + docs in one entity; the
      // size reported back is the sum so downstream analytics (e.g.
      // `SourceRecommendationApplied.total_domains_in_collection`) sees
      // the true cardinality of what's now in source control. The
      // domain-only slice (`state.addedDomains`) is forwarded as
      // `savedDomains` so banner callers can diff against their
      // original recommendation; document additions don't participate
      // in that diff and are reflected only in `domainCount`.
      onCollectionCreated?.({
        id: collection.collection_id,
        type: 'documents',
        domainCount: totalItems,
        savedDomains: state.addedDomains,
      });
      onClose();
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        setError(t('library.wizard.duplicateError'));
      } else {
        toast({
          tone: 'destructive',
          title: t('library.wizard.createError'),
          description: extractAxiosErrorDetail(err),
        });
      }
    }
  }, [
    state.name,
    state.addedDomains,
    state.stagedFiles,
    state.selectedExistingDocs,
    defaultName,
    createCollection,
    teamId,
    kind,
    uploadStagedFilesToCollection,
    addDocsToCollection,
    t,
    onCollectionCreated,
    onClose,
  ]);

  // ---------------------------------------------------------------------------
  // Reset
  // ---------------------------------------------------------------------------

  const reset = useCallback(() => {
    setState({
      step: initialStep,
      name: initialName,
      isTeamCollection: defaultTeam && canCreateTeamCollection,
      addedDomains: initialDomains.slice(0, maxDomains),
      stagedFiles: [],
      selectedExistingDocs: [],
    });
    setError(null);
    setIsCapDialogOpen(false);
  }, [
    initialStep,
    initialName,
    defaultTeam,
    initialDomains,
    maxDomains,
    canCreateTeamCollection,
  ]);

  const dismissCapDialog = useCallback(() => setIsCapDialogOpen(false), []);

  return {
    // State
    state,
    displayName,
    error,
    hasEntryStep,
    isSaving,
    // Per-collection cap (mirrors BE) + dialog state for the "you can't
    // add more" flow. Consumers render `<CollectionCapDialog>` with this.
    maxDomains,
    isCapDialogOpen,
    dismissCapDialog,

    // Team metadata (for rendering TeamToggle)
    hasTeam,
    canCreateTeamCollection,
    teamName: teamDetails?.team.name ?? teamName,

    // State updaters
    setName,
    setIsTeamCollection,
    addDomain,
    addDomains,
    removeDomain,
    addFiles,
    handleFilesRejected,
    removeFile,
    addExistingDocument,
    removeExistingDocument,
    setStep,

    // Transitions
    selectSourceAndTransition,
    stageFilesAndTransition,
    goBack,

    // Save
    saveWebCollection,
    saveDocumentCollection,
    saveCollection,

    // Reset
    reset,
  };
}
